import React from 'react';
import Image from 'next/image';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { decode } from 'html-entities';
import { FaArrowRight, FaRegClock } from 'react-icons/fa';
import { BenzingaLogoWhite } from '@benzinga/valentyn-icons';

import styles from './ReadNextCard.module.css';

export interface ReadNextCardProps {
  data: ReadNextArticleData;
  variant?: 'banner' | 'card' | 'list';
}

export interface ReadNextArticleData {
  title: string;
  teaser: string;
  url: string;
  image?: string;
  category?: string;
  readTime?: string;
}

export const ReadNextCard: React.FC<ReadNextCardProps> = ({ data, variant }) => {
  return (
    <a className={styles['read-next-card-container']} href={data.url} rel="noreferrer noopener" target="_blank">
      {variant === 'banner' && <BannerVariant data={data} />}
      {variant === 'card' && <CardVariant data={data} />}
      {variant === 'list' && <ListVariant data={data} />}
    </a>
  );
};

const BannerVariant: React.FC<{ data: ReadNextArticleData }> = ({ data }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 px-4 py-2 rounded-r-lg w-full flex flex-col">
      <span className={styles['read-next-card-badge']}>Also Read</span>
      <div className={styles['read-next-card-title']} dangerouslySetInnerHTML={{ __html: sanitizeHTML(data.title) }} />
    </div>
  );
};

const CardVariant: React.FC<{ data: ReadNextArticleData }> = ({ data }) => {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
      <div className="flex flex-col md:flex-row">
        <div className="relative w-full aspect-[16/9] overflow-hidden md:aspect-[16/10] md:w-1/3 md:min-w-[240px] md:min-h-[140px]">
          <Image
            alt={data.title ?? 'read next article thumbnail'}
            fill
            loading="lazy"
            sizes="(max-width: 768px) 100vw, 240px"
            src={data.image || '/next-assets/images/benzinga-post-card-gray-placeholder.svg'}
            style={{ objectFit: 'cover', objectPosition: 'center' }}
          />
          <span aria-hidden="true" className={styles['read-next-card-logo-chip']}>
            <BenzingaLogoWhite className={styles['read-next-card-logo']} />
          </span>
        </div>
        <div className={`md:w-2/3 ${styles['read-next-card-body']}`}>
          {data.category && (
            <span className={styles['read-next-card-eyebrow']}>
              <span className={styles['read-next-card-eyebrow-dot']} />
              {decode(data.category)}
            </span>
          )}
          <h5
            className={`${styles['read-next-card-title']} line-clamp-2 mb-1`}
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(data.title) }}
          ></h5>
          <p
            className={`${styles['read-next-card-teaser']} line-clamp-2`}
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(data.teaser) }}
          ></p>
          <div className="flex flex-row items-center justify-between">
            <div className={styles['read-next-card-time']}>
              {data.readTime && <FaRegClock />}
              <span>{data.readTime || ''}</span>
            </div>
            <span className={styles['read-next-card-cta']}>
              Read this article
              <FaArrowRight />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ListVariant: React.FC<{ data: ReadNextArticleData }> = ({ data }) => {
  return (
    <div className="group border-b border-gray-100 pb-4 mb-4">
      <div className="flex flex-col md:flex-row md:items-center gap-2">
        <div className="md:w-1/3">
          {data.category && (
            <span className={`${styles['read-next-card-badge']} px-3 py-1 rounded-sm bg-gray-100 mb-2 capitalize`}>
              {decode(data.category)}
            </span>
          )}
          <h5
            className={styles['read-next-card-title']}
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(data.title) }}
          ></h5>
          {data.readTime && (
            <div className={styles['read-next-card-time']}>
              <FaRegClock />
              <span>{data.readTime}</span>
            </div>
          )}
        </div>
        <div className="md:w-2/3">
          <p
            className={styles['read-next-card-teaser']}
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(data.teaser) }}
          ></p>
          <span className={styles['read-next-card-link']}>
            Read article
            <FaArrowRight />
          </span>
        </div>
      </div>
    </div>
  );
};
